import { marked } from 'marked'

/**
 * Strips markdown formatting characters (asterisks and underscores) from a string.
 * Removes both single and double asterisks/underscores used for bold/italic formatting.
 *
 * @param text - The text containing markdown formatting
 * @returns The text with markdown formatting characters removed
 *
 * @example
 * ```ts
 * stripMarkdownFormatting('*bold* text') // 'bold text'
 * stripMarkdownFormatting('**bold** text') // 'bold text'
 * stripMarkdownFormatting('_italic_ text') // 'italic text'
 * stripMarkdownFormatting('__bold__ text') // 'bold text'
 * ```
 */
export function stripMarkdownFormatting(text: string): string {
  if (!text) return ''
  return text
    .replace(/\*\*/g, '') // Remove double asterisks (**)
    .replace(/\*/g, '') // Remove single asterisks (*)
    .replace(/__/g, '') // Remove double underscores (__)
    .replace(/_/g, '') // Remove single underscores (_)
}

export function stripMarkdown(text: string): string {
  if (!text) return ''

  const tokens = marked.lexer(text)

  const parseToken = (token: any): string => {
    if (token.type === 'paragraph') {
      return token.tokens ? token.tokens.map(parseToken).join('') + '\n\n' : token.text + '\n\n'
    }
    if (token.type === 'heading') {
      return token.tokens ? token.tokens.map(parseToken).join('') + '\n\n' : token.text + '\n\n'
    }
    if (token.type === 'list') {
      return token.items.map(parseToken).join('') + '\n'
    }
    if (token.type === 'list_item') {
      const content = token.tokens ? token.tokens.map(parseToken).join('').trim() : token.text.trim()
      return `• ${content}\n`
    }
    if (token.type === 'blockquote') {
      return token.tokens ? token.tokens.map(parseToken).join('') : token.text
    }
    if (token.type === 'code') {
      return token.text + '\n\n'
    }
    if (token.type === 'strong' || token.type === 'em' || token.type === 'del') {
      return token.tokens ? token.tokens.map(parseToken).join('') : token.text
    }
    if (token.type === 'link') {
      return token.tokens ? token.tokens.map(parseToken).join('') : token.text
    }
    if (token.type === 'text' || token.type === 'codespan' || token.type === 'escape' || token.type === 'html') {
      if (token.tokens) {
        return token.tokens.map(parseToken).join('')
      }
      return token.text
    }
    if (token.type === 'space') {
      return ''
    }
    if (token.type === 'br') {
      return '\n'
    }

    // Fallback for other types
    if (token.tokens) {
      return token.tokens.map(parseToken).join('')
    }
    return token.text || ''
  }

  return tokens.map(parseToken).join('').trim()
}
